import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';

const toUnixTimeSeconds = (value) => {
  if (value == null) return null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
  }
  if (typeof value === 'string') {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric > 1e12 ? Math.floor(numeric / 1000) : Math.floor(numeric);
    }
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return Math.floor(parsed / 1000);
    }
    return null;
  }
  return null;
};

const toNumeric = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatPrice = (value) => {
  if (value == null || Number.isNaN(value)) return '';
  const precision = Math.abs(value) >= 100 ? 2 : 4;
  return value.toFixed(precision);
};

// Lightweight wrapper around TradingView's Advanced Chart widget.
// Loads tv.js on demand and instantiates a widget inside a unique container.

export default function TradingViewChart({
  symbol,
  interval = 'D',
  height = 460,
  theme: requestedTheme,
  visibleRange = null,
  entryPrice,
  exitPrice,
  entryTime,
  exitTime,
  tradeDirection,
  tradeColor,
  stopPrice,
  targetPrice,
  fills = [],
  showTools = false,
}) {
  const appTheme = useTheme();
  const resolvedTheme = requestedTheme || appTheme.palette.mode;
  const isDarkTheme = resolvedTheme === 'dark';
  const chartColors = useMemo(
    () =>
      isDarkTheme
        ? {
            background: '#131722',
            grid: '#2a2e39',
            text: '#b2b5be',
            watermark: 'rgba(178, 181, 190, 0.14)',
          }
        : {
            background: '#ffffff',
            grid: '#f0f3f5',
            text: '#4a4f55',
            watermark: 'rgba(56, 68, 77, 0.15)',
          },
    [isDarkTheme]
  );
  const containerId = useRef(`tradingview_${Math.random().toString(36).slice(2)}`);
  const widgetRef = useRef(null);
  const pendingRangeRef = useRef(null);
  const prevRangeRef = useRef(null);
  const pendingAnnotationsRef = useRef(null);
  const shapesRef = useRef([]);
  const resizeFrameRef = useRef(null);
  const displayedSymbolMatchesRef = useRef(true);
  const [widgetError, setWidgetError] = React.useState(null);
  const watermarkText = useMemo(() => {
    if (!symbol) return '';
    const pieces = String(symbol).split(':');
    return (pieces[pieces.length - 1] || symbol).toUpperCase();
  }, [symbol]);

  const normalizeVisibleRange = (range) => {
    if (!range) return null;
    const parseTs = (value) => {
      if (!value && value !== 0) return null;
      if (value instanceof Date) {
        const ms = value.getTime();
        return Number.isFinite(ms) ? ms : null;
      }
      if (typeof value === 'number') {
        if (!Number.isFinite(value)) return null;
        // assume milliseconds when large, seconds otherwise
        return value > 1e12 ? value : value * 1000;
      }
      if (typeof value === 'string') {
        const numeric = Number(value);
        if (Number.isFinite(numeric)) {
          return numeric > 1e12 ? numeric : numeric * 1000;
        }
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? null : parsed;
      }
      return null;
    };

    const rawFrom = parseTs(range.from ?? range.start ?? range.entry ?? range.begin);
    const rawTo = parseTs(range.to ?? range.end ?? range.exit ?? range.finish);
    if (rawFrom == null && rawTo == null) return null;

    const fallbackFrom = rawFrom != null ? rawFrom : rawTo != null ? rawTo - 60 * 60 * 1000 : null;
    const fallbackTo = rawTo != null ? rawTo : rawFrom != null ? rawFrom + 60 * 60 * 1000 : null;
    if (fallbackFrom == null || fallbackTo == null) return null;

    const fromSec = Math.floor(fallbackFrom / 1000);
    const toSec = Math.ceil(fallbackTo / 1000);
    if (!Number.isFinite(fromSec) || !Number.isFinite(toSec)) return null;
    if (toSec <= fromSec) {
      return { from: fromSec, to: fromSec + 60 * 60 }; // ensure at least 1 hour window
    }
    return { from: fromSec, to: toSec };
  };

  const rangesEqual = (a, b) => {
    if (!a || !b) return a === b;
    return a.from === b.from && a.to === b.to;
  };

  const clearShapes = useCallback(() => {
    if (!shapesRef.current?.length) return;
    shapesRef.current.forEach((shape) => {
      if (shape && typeof shape.remove === 'function') {
        shape.remove();
      } else if (widgetRef.current?.chart && typeof widgetRef.current.chart === 'function') {
        const chart = widgetRef.current.chart();
        if (chart && shape && typeof chart.removeEntity === 'function') {
          try {
            chart.removeEntity(shape);
          } catch {
            // ignore cleanup errors
          }
        }
      }
    });
    shapesRef.current = [];
  }, []);

  const getActiveChart = useCallback((widget) => {
    if (!widget) return null;
    if (typeof widget.activeChart === 'function') return widget.activeChart();
    if (typeof widget.chart === 'function') return widget.chart();
    return null;
  }, []);

  const syncWidgetSize = useCallback(() => {
    const container = document.getElementById(containerId.current);
    if (!container) return;

    const bounds = container.getBoundingClientRect();
    const width = Math.max(1, Math.floor(bounds.width));
    const chartHeight = Math.max(1, Math.floor(bounds.height));
    const iframe = container.querySelector('iframe');

    if (iframe) {
      iframe.style.display = 'block';
      iframe.style.width = '100%';
      iframe.style.height = '100%';
      iframe.style.maxWidth = 'none';
    }

    const widget = widgetRef.current;
    if (widget && typeof widget.resize === 'function') {
      try {
        widget.resize(width, chartHeight);
      } catch {
        // The widget can briefly reject resize calls while changing symbols.
      }
    }
  }, []);

  useEffect(() => {
    const container = document.getElementById(containerId.current);
    if (!container) return undefined;

    let followUpTimer;
    const queueResize = () => {
      if (resizeFrameRef.current != null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
      }
      resizeFrameRef.current = window.requestAnimationFrame(() => {
        resizeFrameRef.current = null;
        syncWidgetSize();
      });
    };

    const resizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(queueResize)
        : null;
    resizeObserver?.observe(container);

    const mutationObserver =
      typeof MutationObserver === 'function'
        ? new MutationObserver(() => {
            queueResize();
            window.clearTimeout(followUpTimer);
            followUpTimer = window.setTimeout(queueResize, 120);
          })
        : null;
    mutationObserver?.observe(container, { childList: true, subtree: true });

    window.addEventListener('resize', queueResize);
    queueResize();

    return () => {
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', queueResize);
      window.clearTimeout(followUpTimer);
      if (resizeFrameRef.current != null) {
        window.cancelAnimationFrame(resizeFrameRef.current);
        resizeFrameRef.current = null;
      }
    };
  }, [syncWidgetSize]);

  const applyPendingAnnotations = useCallback(() => {
    const pending = pendingAnnotationsRef.current;
    if (!pending) {
      clearShapes();
      return;
    }
    const widget = widgetRef.current;
    const chart = getActiveChart(widget);
    if (!chart || typeof chart.createShape !== 'function') return;

    clearShapes();

    const {
      color,
      entryPrice: normalizedEntryPrice,
      exitPrice: normalizedExitPrice,
      entrySec,
      exitSec,
      entryMarkerShape,
      entryMarkerText,
      exitMarkerShape,
      exitMarkerText,
      entryLabel,
      exitLabel,
      linePoints,
      levelOverlays,
      fillOverlays,
    } = pending;

    const shapes = [];
    const safePush = (shape) => {
      if (shape) shapes.push(shape);
    };

    const anchorTime = entrySec ?? exitSec ?? Math.floor(Date.now() / 1000);

    if (linePoints && linePoints.length === 2) {
      const directionUp = linePoints[1].price >= linePoints[0].price;
      const trend = chart.createShape(linePoints, {
        shape: 'trend_line',
        color,
        linewidth: 2,
        linestyle: 2,
        lock: true,
        disableSelection: true,
        disableUndo: true,
        disableSave: true,
      });
      safePush(trend);

      const arrowShape = directionUp ? 'arrow_up' : 'arrow_down';
      const arrowPoint = directionUp ? linePoints[1] : linePoints[0];
      const result = chart.createShape(
        arrowPoint,
        {
          shape: arrowShape,
          color,
          lock: true,
          disableSelection: true,
          disableUndo: true,
          disableSave: true,
        }
      );
      safePush(result);
    }

    if (!linePoints && normalizedEntryPrice != null) {
      const entryLine = chart.createShape(
        { time: anchorTime, price: normalizedEntryPrice },
        {
          shape: 'horizontal_line',
          text: entryLabel || `Entry @ ${formatPrice(normalizedEntryPrice)}`,
          color,
          linewidth: 2,
          linestyle: 2,
          lock: true,
          disableSelection: true,
          disableUndo: true,
          disableSave: true,
        }
      );
      safePush(entryLine);

      if (entrySec != null) {
        let entryMarker = chart.createShape(
          { time: entrySec, price: normalizedEntryPrice },
          {
            shape: entryMarkerShape,
            text: entryMarkerText,
            color,
            lock: true,
            disableSelection: true,
            disableUndo: true,
            disableSave: true,
          }
        );
        if (!entryMarker && entryMarkerShape !== 'price_label') {
          entryMarker = chart.createShape(
            { time: entrySec, price: normalizedEntryPrice },
            {
              shape: 'price_label',
              text: entryMarkerText,
              color,
              backgroundColor: color,
              lock: true,
              disableSelection: true,
              disableUndo: true,
              disableSave: true,
            }
          );
        }
        safePush(entryMarker);
      }
    }

    if (!linePoints && normalizedExitPrice != null) {
      const exitLine = chart.createShape(
        { time: exitSec ?? anchorTime, price: normalizedExitPrice },
        {
          shape: 'horizontal_line',
          text: exitLabel || `Exit @ ${formatPrice(normalizedExitPrice)}`,
          color,
          linewidth: 2,
          linestyle: 2,
          lock: true,
          disableSelection: true,
          disableUndo: true,
          disableSave: true,
        }
      );
      safePush(exitLine);

      if (exitSec != null) {
        let exitMarker = chart.createShape(
          { time: exitSec, price: normalizedExitPrice },
          {
            shape: exitMarkerShape,
            text: exitMarkerText,
            color,
            lock: true,
            disableSelection: true,
            disableUndo: true,
            disableSave: true,
          }
        );
        if (!exitMarker && exitMarkerShape !== 'price_label') {
          exitMarker = chart.createShape(
            { time: exitSec, price: normalizedExitPrice },
            {
              shape: 'price_label',
              text: exitMarkerText,
              color,
              backgroundColor: color,
              lock: true,
              disableSelection: true,
              disableUndo: true,
              disableSave: true,
            }
          );
        }
        safePush(exitMarker);
      }
    }

    levelOverlays.forEach((overlay) => {
      const shape = chart.createShape(
        { time: overlay.time ?? anchorTime, price: overlay.price },
        {
          shape: 'horizontal_line',
          text: `${overlay.label} @ ${formatPrice(overlay.price)}`,
          color: overlay.color,
          linewidth: 2,
          linestyle: 2,
          lock: true,
          disableSelection: true,
          disableUndo: true,
          disableSave: true,
        }
      );
      safePush(shape);
    });

    fillOverlays.forEach((fill) => {
      const marker = chart.createShape(
        { time: fill.time, price: fill.price },
        {
          shape: 'price_label',
          text: fill.label,
          color: fill.color,
          backgroundColor: fill.color,
          lock: true,
          disableSelection: true,
          disableUndo: true,
          disableSave: true,
        }
      );
      safePush(marker);
    });

    shapesRef.current = shapes;
  }, [clearShapes, getActiveChart]);

  useEffect(() => {
    if (!symbol) {
      displayedSymbolMatchesRef.current = false;
      setWidgetError(null);
      return undefined;
    }
    let scriptEl;
    let widgetInstance;
    let scriptErrorHandler;
    displayedSymbolMatchesRef.current = true;
    setWidgetError(null);

    const applyPendingRange = () => {
      const pending = pendingRangeRef.current;
      if (!pending) return;
      const chart = getActiveChart(widgetInstance);
      if (chart && typeof chart.setVisibleRange === 'function') {
        chart.setVisibleRange(pending);
      }
    };

    const instantiateWidget = () => {
      if (!window.TradingView || !window.TradingView.widget) {
        setWidgetError('TradingView is unavailable.');
        return;
      }
      try {
        widgetInstance = new window.TradingView.widget({
        autosize: true,
        symbol,
        interval,
        timezone: 'Etc/UTC',
        theme: resolvedTheme,
        style: '1',
        locale: 'en',
        enable_publishing: false,
        allow_symbol_change: showTools,
        hide_top_toolbar: !showTools,
        hide_side_toolbar: !showTools,
        withdateranges: true,
        hide_legend: false,
        details: showTools,
        calendar: showTools,
        studies: showTools ? ['Volume@tv-basicstudies'] : [],
        container_id: containerId.current,
        showSymbolLogo: true,
        toolbar_bg: chartColors.background,
        loading_screen: {
          backgroundColor: chartColors.background,
          foregroundColor: '#01a8f8',
        },
        enabled_features: showTools ? ['study_templates'] : [],
        disabled_features: showTools
          ? ['header_saveload']
          : [
              'header_compare',
              'header_symbol_search',
              'header_undo_redo',
              'header_saveload',
              'header_interval_dialog_button',
              'timeframes_toolbar',
              'create_volume_indicator_by_default',
              'volume_force_overlay',
            ],
        overrides: {
          'paneProperties.background': chartColors.background,
          'paneProperties.backgroundType': 'solid',
          'paneProperties.vertGridProperties.color': chartColors.grid,
          'paneProperties.horzGridProperties.color': chartColors.grid,
          'scalesProperties.textColor': chartColors.text,
          'watermarkProperties.color': chartColors.watermark,
          'watermarkProperties.visible': true,
          'watermarkProperties.text': watermarkText,
          'watermarkProperties.transparency': 85,
        },
        });
      } catch {
        setWidgetError('TradingView could not initialize this symbol.');
        return;
      }
      widgetRef.current = widgetInstance;
      if (typeof widgetInstance.onChartReady === 'function') {
        widgetInstance.onChartReady(() => {
          syncWidgetSize();
          window.requestAnimationFrame(syncWidgetSize);
          window.setTimeout(syncWidgetSize, 120);
          window.setTimeout(syncWidgetSize, 320);
          applyPendingRange();
          applyPendingAnnotations();
          if (typeof widgetInstance.applyOverrides === 'function') {
            widgetInstance.applyOverrides({ 'watermarkProperties.text': watermarkText });
          }
          const chart = getActiveChart(widgetInstance);
          if (chart && typeof chart.onSymbolChanged === 'function') {
            chart.onSymbolChanged((symbolInfo) => {
              const displayed = symbolInfo?.full_name || symbolInfo?.ticker || symbolInfo?.name || '';
              const expected = String(symbol).toUpperCase();
              const normalizedDisplayed = String(displayed).toUpperCase();
              displayedSymbolMatchesRef.current = normalizedDisplayed === expected || expected.endsWith(`:${normalizedDisplayed}`);
              if (displayedSymbolMatchesRef.current) applyPendingAnnotations();
              else clearShapes();
            });
          }
          if (chart && typeof chart.onDataLoaded === 'function') {
            const dataLoaded = chart.onDataLoaded();
            if (dataLoaded && typeof dataLoaded.subscribe === 'function') {
              dataLoaded.subscribe(undefined, () => {
                if (displayedSymbolMatchesRef.current) applyPendingAnnotations();
              });
            }
          }
        });
      }
    };

    const existingScript = document.querySelector('script[data-tradingview-widget]');
    if (window.TradingView && window.TradingView.widget) {
      instantiateWidget();
    } else if (existingScript) {
      existingScript.addEventListener('load', instantiateWidget, { once: true });
    } else {
      scriptEl = document.createElement('script');
      scriptEl.src = 'https://s3.tradingview.com/tv.js';
      scriptEl.type = 'text/javascript';
      scriptEl.async = true;
      scriptEl.setAttribute('data-tradingview-widget', 'true');
      scriptEl.addEventListener('load', instantiateWidget, { once: true });
      scriptErrorHandler = () => setWidgetError('TradingView failed to load.');
      scriptEl.addEventListener('error', scriptErrorHandler, { once: true });
      document.body.appendChild(scriptEl);
    }

    return () => {
      if (existingScript) {
        existingScript.removeEventListener('load', instantiateWidget);
      }
      if (scriptEl) {
        scriptEl.removeEventListener('load', instantiateWidget);
        if (scriptErrorHandler) scriptEl.removeEventListener('error', scriptErrorHandler);
      }
      try {
        if (widgetInstance && typeof widgetInstance.remove === 'function') {
          widgetInstance.remove();
        }
      } catch {
        // Ignore removal errors (widget already detached)
      }
      widgetRef.current = null;
      const el = document.getElementById(containerId.current);
      if (el) {
        el.innerHTML = '';
      }
      clearShapes();
    };
  }, [
    symbol,
    interval,
    resolvedTheme,
    chartColors,
    watermarkText,
    showTools,
    applyPendingAnnotations,
    clearShapes,
    getActiveChart,
    syncWidgetSize,
  ]);

  useEffect(() => {
    const normalized = normalizeVisibleRange(visibleRange);
    if (!normalized) {
      pendingRangeRef.current = null;
      prevRangeRef.current = null;
      return;
    }
    if (rangesEqual(prevRangeRef.current, normalized)) return;
    prevRangeRef.current = normalized;
    pendingRangeRef.current = normalized;

    const widget = widgetRef.current;
    const chart = getActiveChart(widget);
    if (chart && typeof chart.setVisibleRange === 'function') {
      chart.setVisibleRange(normalized);
    } else if (widget && typeof widget.onChartReady === 'function') {
      widget.onChartReady(() => {
        const readyChart = widget.chart && widget.chart();
        if (readyChart && typeof readyChart.setVisibleRange === 'function') {
          readyChart.setVisibleRange(normalized);
        }
      });
    }
  }, [visibleRange, getActiveChart]);

  const annotationData = useMemo(() => {
    const normalizedEntryPrice = toNumeric(entryPrice);
    const normalizedExitPrice = toNumeric(exitPrice);
    const normalizedStopPrice = toNumeric(stopPrice);
    const normalizedTargetPrice = toNumeric(targetPrice);
    const entrySec = toUnixTimeSeconds(entryTime);
    const exitSec = toUnixTimeSeconds(exitTime);
    const fillOverlays = (Array.isArray(fills) ? fills : [])
      .map((fill) => {
        const price = toNumeric(fill?.price);
        const time = toUnixTimeSeconds(fill?.timestamp);
        if (price == null || time == null) return null;
        const side = String(fill?.side || '').toUpperCase();
        return {
          price,
          time,
          label: `${side || 'FILL'}${fill?.quantity != null ? ` ${fill.quantity}` : ''} @ ${formatPrice(price)}`,
          color: side === 'SELL' ? '#e05656' : '#01A8F8',
        };
      })
      .filter(Boolean);
    if (normalizedEntryPrice == null && normalizedExitPrice == null && normalizedStopPrice == null && normalizedTargetPrice == null && fillOverlays.length === 0) return null;

    const direction = (tradeDirection || '').toUpperCase();
    const color = tradeColor || '#0288d1';

    const entryMarkerShape = direction === 'SHORT' ? 'arrow_down' : 'arrow_up';
    const exitMarkerShape = direction === 'SHORT' ? 'arrow_up' : 'arrow_down';
    const entryMarkerText = direction === 'SHORT' ? 'SELL' : 'BUY';
    const exitMarkerText = direction === 'SHORT' ? 'BUY' : 'SELL';

    const linePoints =
      normalizedEntryPrice != null && normalizedExitPrice != null && entrySec != null && exitSec != null
        ? [
            { time: entrySec, price: normalizedEntryPrice },
            { time: exitSec, price: normalizedExitPrice },
          ]
        : null;

    return {
      color,
      entryPrice: normalizedEntryPrice,
      exitPrice: normalizedExitPrice,
      entrySec,
      exitSec,
      entryMarkerShape,
      exitMarkerShape,
      entryMarkerText,
      exitMarkerText,
      entryLabel: normalizedEntryPrice != null ? `${entryMarkerText} @ ${formatPrice(normalizedEntryPrice)}` : undefined,
      exitLabel: normalizedExitPrice != null ? `${exitMarkerText} @ ${formatPrice(normalizedExitPrice)}` : undefined,
      linePoints,
      levelOverlays: [
        normalizedStopPrice == null ? null : { label: 'Stop', price: normalizedStopPrice, color: '#e05656', time: entrySec },
        normalizedTargetPrice == null ? null : { label: 'Target', price: normalizedTargetPrice, color: '#16b364', time: entrySec },
      ].filter(Boolean),
      fillOverlays,
    };
  }, [entryPrice, exitPrice, stopPrice, targetPrice, fills, entryTime, exitTime, tradeDirection, tradeColor]);

  useEffect(() => {
    pendingAnnotationsRef.current = annotationData;
    if (!annotationData) {
      clearShapes();
      return;
    }
    applyPendingAnnotations();
  }, [annotationData, applyPendingAnnotations, clearShapes]);

  return (
    <div
      style={{
        display: 'block',
        position: 'relative',
        width: '100%',
        minWidth: 0,
        height,
        overflow: 'hidden',
        backgroundColor: chartColors.background,
        colorScheme: resolvedTheme,
      }}
    >
      <div id={containerId.current} style={{ width: '100%', height: '100%' }} />
      {!symbol || widgetError ? (
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center', color: chartColors.text, background: chartColors.background }}>
          {widgetError || 'Chart unavailable: this asset has no authoritative TradingView symbol.'}
        </div>
      ) : null}
    </div>
  );
}

TradingViewChart.propTypes = {
  symbol: PropTypes.string,
  interval: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  theme: PropTypes.oneOf(['light', 'dark']),
  visibleRange: PropTypes.shape({
    from: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
    to: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)])
  }),
  entryPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  exitPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  entryTime: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
  exitTime: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
  tradeDirection: PropTypes.string,
  tradeColor: PropTypes.string,
  stopPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  targetPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fills: PropTypes.arrayOf(PropTypes.shape({
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    timestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
    quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    side: PropTypes.string,
  })),
  showTools: PropTypes.bool,
};
